<script >
import { use } from '@fegobull/webapi';
import { computed, ref, watch } from 'vue-demi';
import { useFegoPageInfo } from '../common';
import { GuessUpDownValue } from '../constants';
import locales from '../locales';
import { formatI18n } from '../utils/i18n';
const __sfc_main = {};
__sfc_main.props = {
  // 竞猜场次信息
  session: {
    type: Object,
    required: true
  },
  // 当前用户的竞猜记录
  record: {
    type: Object,
    default: null
  },
  // 活动标识
  actSign: {
    type: String,
    required: true
  },
  // 提交状态
  submitting: {
    type: Boolean,
    default: false
  },
  // 错误信息
  errorMsg: {
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const {
    useI18n
  } = use;
  const $t = useI18n(locales);
  const {
    kls
  } = useFegoPageInfo();
  const selectedValue = ref(null);

  // 同步选择状态：record 变化时回填；record 为空时清空
  watch(() => props.record?.guess_updown, v => {
    if (v != null && Number(v) > 0) {
      selectedValue.value = Number(v);
      return;
    }
    selectedValue.value = null;
  }, {
    immediate: true
  });

  // 是否可以提交
  const canSubmit = computed(() => {
    return selectedValue.value !== null && !props.submitting;
  });

  // 参与统计展示：“已有23456人参与，85%的牛友认为会上涨”
  const statText = computed(() => {
    const n = Number(props.session?.participateCount ?? 0);
    const pct = Number(props.session?.trendPercent ?? NaN);
    const dir = String(props.session?.trendDirection ?? '');
    if (!Number.isFinite(n) || n <= 0) return '';
    if (!Number.isFinite(pct)) return '';
    if (dir !== 'up' && dir !== 'down') return '';
    const countStr = n > 100000 ? '10w+' : String(n);
    const pctStr = String(Math.max(0, Math.min(100, Math.round(pct))));
    const trendWord = dir === 'up' ? String($t('statUpWord')) : String($t('statDownWord'));
    // 说明：使用方案 B（模板插值）减少 prefix/suffix 拼接
    const tpl = String($t('statTextTpl'));
    return formatI18n(tpl, {
      count: countStr,
      pct: pctStr,
      trend: trendWord
    });
  });

  // 最新提交的 3 人（头像由统计接口返回）
  const latestSubmitters = computed(() => Array.isArray(props.session?.latestSubmitters) ? props.session.latestSubmitters : []);

  // 提交竞猜
  const handleSubmit = () => {
    if (!canSubmit.value) return;
    emit('submit', {
      guessUpdown: selectedValue.value
    });
  };

  // 选择涨跌
  const selectValue = value => {
    selectedValue.value = value;
  };
  return {
    GuessUpDownValue,
    props,
    $t,
    kls,
    selectedValue,
    canSubmit,
    statText,
    latestSubmitters,
    handleSubmit,
    selectValue
  };
};
export default __sfc_main;
</script>

<template>
    <div class="up-down-guess" :class="kls">
        <div class="session-info">
            <div class="session-title">{{ $t('upDownTitle') }}</div>
            <div v-if="statText" class="stat-text">
                <span v-if="latestSubmitters.length" class="latest-avatars">
                    <img
                        v-for="x in latestSubmitters"
                        :key="String(x.cid || x.avatar || '')"
                        class="avatar"
                        :src="x.avatar"
                        alt=""
                    />
                </span>
                <span>{{ statText }}</span>
            </div>
            <div v-if="record" class="status-text submitted">{{ $t('submitted') }}</div>
        </div>

        <div class="guess-options">
            <button
                class="option-btn"
                :class="{
                    'selected': selectedValue === GuessUpDownValue.Up,
                    'disabled': props.submitting
                }"
                @click="selectValue(GuessUpDownValue.Up)"
            >
                <span class="icon up">↑</span>
                <span class="text">{{ $t('upText') }}</span>
            </button>

            <button
                class="option-btn"
                :class="{
                    'selected': selectedValue === GuessUpDownValue.Down,
                    'disabled': props.submitting
                }"
                @click="selectValue(GuessUpDownValue.Down)"
            >
                <span class="icon down">↓</span>
                <span class="text">{{ $t('downText') }}</span>
            </button>
        </div>

        <!-- 错误提示 -->
        <div v-if="props.errorMsg" class="error-msg">{{ props.errorMsg }}</div>

        <!-- 提交按钮 -->
        <button
            class="submit-btn"
            :class="{ 'disabled': !canSubmit }"
            :disabled="!canSubmit"
            @click="handleSubmit"
        >
            {{ props.submitting ? $t('submittingText') : (record ? $t('modifyGuess') : $t('submitGuess')) }}
        </button>

        <!-- 已提交的记录显示 -->
        <div v-if="record && record.guess_updown" class="record-info">
            <div class="record-label">{{ $t('yourGuessLabel') }}</div>
            <div class="record-value" :class="record.guess_updown === GuessUpDownValue.Up ? 'up' : 'down'">
                {{ record.guess_updown === GuessUpDownValue.Up ? $t('upText') : $t('downText') }}
            </div>
        </div>
    </div>
</template>

<style scoped lang="less">
.up-down-guess {
    padding: 16px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    .session-info {
        margin-bottom: 16px;

        .session-title {
            font-size: 16px;
            font-weight: bold;
            color: #333;
            margin-bottom: 8px;
        }

        .stat-text {
            font-size: 12px;
            line-height: 16px;
            color: #666;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 8px;

            .latest-avatars {
                display: inline-flex;
                align-items: center;
                .avatar {
                    width: 14px;
                    height: 14px;
                    border-radius: 50%;
                    object-fit: cover;
                    border: 1px solid rgba(255, 255, 255, 0.8);
                    margin-left: -4px;
                    background: #f2f2f2;
                }
                .avatar:first-child {
                    margin-left: 0;
                }
            }
        }

        .status-text {
            font-size: 12px;
            color: #999;

            &.expired {
                color: #f56c6c;
            }

            &.not-started {
                color: #409eff;
            }

            &.submitted {
                color: #67c23a;
            }
        }
    }

    .guess-options {
        display: flex;
        gap: 12px;
        margin-bottom: 16px;

        .option-btn {
            flex: 1;
            padding: 16px;
            border: 2px solid #e4e7ed;
            border-radius: 8px;
            background: #fff;
            cursor: pointer;
            transition: all 0.3s;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;

            &:hover:not(.disabled) {
                border-color: #409eff;
                background: #ecf5ff;
            }

            &.selected {
                border-color: #409eff;
                background: #ecf5ff;

                .icon.up {
                    color: #67c23a;
                }

                .icon.down {
                    color: #f56c6c;
                }
            }

            &.disabled {
                cursor: not-allowed;
                opacity: 0.6;
            }

            .icon {
                font-size: 24px;
                font-weight: bold;

                &.up {
                    color: #67c23a;
                }

                &.down {
                    color: #f56c6c;
                }
            }

            .text {
                font-size: 14px;
                color: #333;
            }
        }
    }

    .error-msg {
        margin-bottom: 12px;
        padding: 8px 12px;
        background: #fef0f0;
        border: 1px solid #fde2e2;
        border-radius: 4px;
        color: #f56c6c;
        font-size: 12px;
    }

    .submit-btn {
        width: 100%;
        padding: 12px;
        background: #409eff;
        color: #fff;
        border: none;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.3s;

        &:hover:not(.disabled) {
            background: #66b1ff;
        }

        &.disabled {
            background: #c0c4cc;
            cursor: not-allowed;
        }
    }

    .record-info {
        margin-top: 16px;
        padding: 12px;
        background: #f5f7fa;
        border-radius: 4px;

        .record-label {
            font-size: 12px;
            color: #909399;
            margin-bottom: 4px;
        }

        .record-value {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 8px;

            &.up {
                color: #67c23a;
            }

            &.down {
                color: #f56c6c;
            }
        }
    }
}
</style>

